import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, Fire, Medal, Users, CaretDown } from "@phosphor-icons/react";
import { AttendanceHistoryDialog } from "@/components/AttendanceHistoryDialog";
import { toast } from "sonner";

const dayWord = (n) => (n === 1 ? "day" : "days");
const RANK_ACCENT = ["text-amber-500", "text-slate-400", "text-[#b45309]"];

const RankingTable = ({ rows, isAdmin, me, onRowClick, testId, rowPrefix }) => (
    <div className="overflow-x-auto">
        <Table data-testid={testId}>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    {isAdmin && <TableHead className="text-right">Longest</TableHead>}
                    {isAdmin && <TableHead className="text-right">Worked days</TableHead>}
                    {isAdmin && <TableHead className="text-right">Absences</TableHead>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((r) => {
                    const mine = me && r.technician_id === me.technician_id;
                    return (
                        <TableRow
                            key={r.technician_id}
                            className={`${mine ? "bg-blue-50" : ""} ${isAdmin ? "cursor-pointer hover:bg-slate-50" : ""}`}
                            onClick={isAdmin ? () => onRowClick(r.technician_id) : undefined}
                            data-testid={`${rowPrefix}-${r.rank}`}
                        >
                            <TableCell className="font-mono text-sm">
                                <span className="inline-flex items-center gap-1.5">
                                    {r.rank <= 3
                                        ? <Medal size={16} weight="fill" className={RANK_ACCENT[r.rank - 1]} />
                                        : <span className="w-4" />}
                                    #{r.rank}
                                </span>
                            </TableCell>
                            <TableCell className={`font-medium ${mine ? "text-[#003366]" : "text-[#1F2A44]"}`}>
                                {r.full_name}
                                {mine && <span className="text-xs text-[#0066D9] ml-1.5">(you)</span>}
                            </TableCell>
                            <TableCell className="text-right">
                                <span className="inline-flex items-center gap-1 font-semibold text-[#003366]">
                                    <Fire size={15} weight="fill" className="text-[#E6002C]" />
                                    {r.current_streak}
                                </span>
                            </TableCell>
                            {isAdmin && (
                                <TableCell className="text-right">
                                    <span className="inline-flex items-center gap-1 text-slate-700">
                                        <Trophy size={14} weight="fill" className="text-amber-500" />
                                        {r.longest_streak}
                                    </span>
                                </TableCell>
                            )}
                            {isAdmin && <TableCell className="text-right text-slate-600">{r.total_worked_days}</TableCell>}
                            {isAdmin && (
                                <TableCell className="text-right">
                                    <span className={r.absent_days > 0 ? "text-[#E6002C] font-medium" : "text-slate-400"}>
                                        {r.absent_days}
                                    </span>
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </div>
);

export default function Leaderboard() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailId, setDetailId] = useState(null);
    const [full, setFull] = useState(null);
    const [fullOpen, setFullOpen] = useState(false);
    const [fullLoading, setFullLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/streaks/leaderboard?limit=10")
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const showFull = async () => {
        setFullOpen(true);
        if (full) return;
        setFullLoading(true);
        try {
            const { data: d } = await api.get("/streaks/ranking");
            setFull(d);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setFullOpen(false);
        } finally {
            setFullLoading(false);
        }
    };

    const rows = Array.isArray(data?.top) ? data.top : [];
    const me = data?.me || null;
    const eligible = data?.eligible_total ?? 0;

    return (
        <div className="space-y-8">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Recognition</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Top 10 Work Streaks
                </h1>
                <p className="text-slate-600 mt-2 text-sm">
                    Consecutive worked days. Approved time off and no-workload days are neutral — only an
                    unexcused absence resets a streak.
                </p>
            </div>

            {/* Personal status — always visible, even outside the Top 10 */}
            {me && (
                <Card className="border-[#E2E8F0] bg-gradient-to-br from-[#003366] to-[#0066D9] text-white"
                    data-testid="my-streak-panel">
                    <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">Your current streak</div>
                            <div className="flex items-center gap-2">
                                <Fire size={26} weight="fill" className="text-[#FFB4C0]" />
                                <span className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}
                                    data-testid="my-streak-days">{me.current_streak}</span>
                                <span className="text-sm text-white/80">{dayWord(me.current_streak)}</span>
                            </div>
                        </div>
                        <div className="sm:border-l sm:border-white/20 sm:pl-6">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">Your rank</div>
                            <div className="text-3xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="my-streak-rank">
                                #{me.rank}
                            </div>
                            <div className="text-xs text-white/70 mt-0.5">of {eligible} technicians</div>
                        </div>
                        {data.in_top && (
                            <Badge className="bg-white/15 border border-white/30 text-white w-fit sm:ml-auto"
                                data-testid="my-streak-in-top">In the Top 10</Badge>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card className="border-[#E2E8F0]">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6"><Skeleton className="h-64 rounded-md" /></div>
                    ) : rows.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-500" data-testid="leaderboard-empty">
                            No work streaks recorded yet.
                        </div>
                    ) : (
                        <RankingTable rows={rows} isAdmin={isAdmin} me={me} onRowClick={setDetailId}
                            testId="leaderboard-table" rowPrefix="leaderboard-row" />
                    )}
                </CardContent>
            </Card>

            {/* Full ranking — every eligible technician, #1 through #N */}
            {!fullOpen ? (
                <Button variant="outline" onClick={showFull} disabled={loading || eligible === 0}
                    className="border-[#0066D9] text-[#0066D9] hover:bg-blue-50 w-full sm:w-auto"
                    data-testid="view-full-ranking-button">
                    View Full Ranking <CaretDown size={14} className="ml-1.5" />
                </Button>
            ) : (
                <div className="space-y-4" data-testid="full-ranking-section">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <h2 className="text-lg md:text-lg font-semibold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                Full Attendance Ranking
                            </h2>
                            <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1"
                                data-testid="full-ranking-count">
                                <Users size={15} className="text-[#0066D9]" />
                                {full?.eligible_total ?? eligible} Active Technicians
                            </div>
                        </div>
                        <Button variant="ghost" className="text-slate-500" onClick={() => setFullOpen(false)}
                            data-testid="hide-full-ranking-button">Hide</Button>
                    </div>
                    <Card className="border-[#E2E8F0]">
                        <CardContent className="p-0">
                            {fullLoading || !full ? (
                                <div className="p-6"><Skeleton className="h-64 rounded-md" /></div>
                            ) : (
                                <RankingTable rows={full.rows} isAdmin={isAdmin} me={full.me} onRowClick={setDetailId}
                                    testId="full-ranking-table" rowPrefix="full-ranking-row" />
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {isAdmin && (
                <p className="text-xs text-slate-400">
                    Click an employee to open their detailed attendance history. Employees switched off in
                    Attendance &amp; Recognition are skipped by every ranking.
                </p>
            )}

            {isAdmin && (
                <AttendanceHistoryDialog
                    technicianId={detailId}
                    open={Boolean(detailId)}
                    onOpenChange={(o) => { if (!o) setDetailId(null); }}
                />
            )}
        </div>
    );
}
